import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { IconAlertCircle, IconPlus } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { CPG_MEASURE_KIND_VALUES } from '@behuman/core-contract'
import { useOrpc } from '@/hooks/useClient'
import { GuidelineBasis } from './GuidelineBasis'
import { ProposalsView } from './ProposalsView'
import {
  MEASURE_KIND_COLORS,
  MEASURE_STATUS_COLORS,
  actorLabel,
  useCpgFamilies,
  useCpgMeasures,
  type CpgFamilyGroup,
  type CpgMeasure,
} from './hooks'

const KIND_OPTIONS = CPG_MEASURE_KIND_VALUES.map((kind) => ({ value: kind, label: kind }))

function MeasureTree({
  groups,
  measures,
  selectedId,
  onSelect,
}: {
  groups: CpgFamilyGroup[]
  measures: CpgMeasure[]
  selectedId: number | null
  onSelect: (id: number) => void
}) {
  return (
    <nav className="p-2">
      {groups.map((group) => (
        <div key={group.id}>
          <div className="px-2 py-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
            {group.label}
          </div>
          {group.families.map((family) => {
            const own = measures.filter((m) => m.familyId === family.id)
            return (
              <div key={family.id}>
                <div className="flex items-center px-2 py-1 pl-4 text-sm text-gray-700">
                  <span className="truncate">{family.label}</span>
                  <span className="ml-auto shrink-0 text-xs text-gray-400">{own.length}</span>
                </div>
                {own.map((measure) => (
                  <button
                    key={measure.id}
                    type="button"
                    onClick={() => onSelect(measure.id)}
                    className={`flex w-full items-center gap-1 truncate rounded px-2 py-1 pl-8 text-left text-sm hover:bg-gray-100 ${
                      selectedId === measure.id ? 'bg-gray-200 font-medium' : ''
                    } ${measure.status === 'active' ? '' : 'text-gray-400'}`}
                  >
                    <Badge
                      size="xs"
                      variant="light"
                      radius="sm"
                      color={MEASURE_KIND_COLORS[measure.kind]}
                      className="shrink-0"
                    >
                      {measure.kind}
                    </Badge>
                    <span className="truncate">{measure.label}</span>
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      ))}
    </nav>
  )
}

function CreateMeasureModal({
  groups,
  onClose,
  onCreated,
}: {
  groups: CpgFamilyGroup[]
  onClose: () => void
  onCreated: (id: number) => void
}) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [familyId, setFamilyId] = useState<string | null>(null)
  const [kind, setKind] = useState<string | null>(null)
  const [slug, setSlug] = useState('')
  const [label, setLabel] = useState('')
  const [definition, setDefinition] = useState('')
  const [gradingRule, setGradingRule] = useState('')

  const create = useMutation(
    orpc.createCpgMeasure.mutationOptions({
      onSuccess: async ({ data }) => {
        await queryClient.invalidateQueries({ queryKey: orpc.listCpgMeasures.key() })
        onCreated(data.id)
        onClose()
      },
    }),
  )

  const familyOptions = groups.map((g) => ({
    group: g.label,
    items: g.families.filter((f) => f.active).map((f) => ({ value: String(f.id), label: f.label })),
  }))
  const slugOk = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)
  const ready =
    familyId !== null &&
    kind !== null &&
    slugOk &&
    label.trim() !== '' &&
    definition.trim() !== '' &&
    gradingRule.trim() !== ''

  return (
    <Modal opened onClose={onClose} title={<Text fw={600}>New measure</Text>} size="lg">
      <Stack gap="sm">
        <Select
          label="Family"
          description="Immutable once created"
          data={familyOptions}
          value={familyId}
          onChange={setFamilyId}
          searchable
        />
        <Select
          label="Kind"
          description="risk = something to flag; gap = care that is due — immutable once created"
          data={KIND_OPTIONS}
          value={kind}
          onChange={setKind}
        />
        <TextInput
          label="Slug"
          description="kebab-case, the agent-facing identity — immutable once created"
          placeholder="a1c-overdue"
          value={slug}
          onChange={(e) => setSlug(e.currentTarget.value)}
          error={slug !== '' && !slugOk ? 'kebab-case: a-z, 0-9, dashes' : undefined}
        />
        <TextInput
          label="Label"
          placeholder="HbA1c overdue"
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
        />
        <Textarea
          label="Definition"
          description="What qualifies a patient — read by the Risks & Gaps agent"
          autosize
          minRows={3}
          value={definition}
          onChange={(e) => setDefinition(e.currentTarget.value)}
        />
        <Textarea
          label="Grading rule"
          description="When a finding is high vs medium — read by the agent"
          autosize
          minRows={2}
          value={gradingRule}
          onChange={(e) => setGradingRule(e.currentTarget.value)}
        />
        {create.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {create.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            loading={create.isPending}
            onClick={() =>
              create.mutate({
                familyId: Number(familyId),
                kind: kind as CpgMeasure['kind'],
                slug,
                label: label.trim(),
                definition: definition.trim(),
                gradingRule: gradingRule.trim(),
              })
            }
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function MeasureDetail({ measure }: { measure: CpgMeasure }) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [label, setLabel] = useState(measure.label)
  const [definition, setDefinition] = useState(measure.definition)
  const [gradingRule, setGradingRule] = useState(measure.gradingRule)

  // The parent keys this editor by measure id, so picking another measure starts fresh. Within
  // one measure a refetch (a Retire/Reactivate, another admin's save, a window-focus refetch that
  // brought changes) replaces only the fields the user has not touched, so an unsaved draft
  // survives it. Save adopts what the server stored (see its onSuccess).
  const server = useRef(measure)
  useEffect(() => {
    const prev = server.current
    server.current = measure
    setLabel((cur) => (cur === prev.label ? measure.label : cur))
    setDefinition((cur) => (cur === prev.definition ? measure.definition : cur))
    setGradingRule((cur) => (cur === prev.gradingRule ? measure.gradingRule : cur))
  }, [measure])

  const update = useMutation(
    orpc.updateCpgMeasure.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.listCpgMeasures.key() })
      },
    }),
  )

  const dirty =
    label !== measure.label ||
    definition !== measure.definition ||
    gradingRule !== measure.gradingRule

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-4">
      <Group justify="space-between" align="flex-start">
        <div>
          <Group gap="xs">
            <Badge variant="light" color="blue" radius="sm">
              {measure.slug}
            </Badge>
            <Badge variant="light" color={MEASURE_KIND_COLORS[measure.kind]} radius="sm">
              {measure.kind}
            </Badge>
            <Badge variant="light" color={MEASURE_STATUS_COLORS[measure.status]} radius="sm">
              {measure.status}
            </Badge>
            <Badge variant="outline" color="gray" radius="sm">
              {measure.origin}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed" className="mt-1">
            #{measure.id} · {measure.familyLabel} · updated{' '}
            {new Date(measure.updatedAt).toLocaleString()} by {actorLabel(measure.updatedBy)}
          </Text>
        </div>
        <Button
          variant="default"
          size="compact-sm"
          loading={update.isPending}
          onClick={() =>
            update.mutate({
              id: measure.id,
              status: measure.status === 'active' ? 'retired' : 'active',
            })
          }
        >
          {measure.status === 'active' ? 'Retire' : 'Reactivate'}
        </Button>
      </Group>
      {measure.rationale && (
        <Text size="sm" c="dimmed" className="mt-2 italic">
          Proposer&apos;s rationale: {measure.rationale}
        </Text>
      )}
      <GuidelineBasis citations={measure.citations} />
      <Stack gap="xs" className="mt-3">
        <TextInput label="Label" value={label} onChange={(e) => setLabel(e.currentTarget.value)} />
        <Textarea
          label="Definition"
          description="What qualifies a patient — read by the Risks & Gaps agent; edits apply to future reports"
          autosize
          minRows={3}
          maxRows={12}
          value={definition}
          onChange={(e) => setDefinition(e.currentTarget.value)}
        />
        <Textarea
          label="Grading rule"
          description="When a finding is high vs medium — read by the agent"
          autosize
          minRows={2}
          maxRows={8}
          value={gradingRule}
          onChange={(e) => setGradingRule(e.currentTarget.value)}
        />
        {update.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {update.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            disabled={
              !dirty || label.trim() === '' || definition.trim() === '' || gradingRule.trim() === ''
            }
            loading={update.isPending}
            onClick={() => {
              // What the fields held when Save was clicked. The inputs stay editable while the
              // request is in flight, so on success only a field still holding exactly this
              // text adopts the stored (trimmed) value; anything typed since is kept as a draft.
              const sent = { label, definition, gradingRule }
              update.mutate(
                {
                  id: measure.id,
                  ...(label !== measure.label ? { label: label.trim() } : {}),
                  ...(definition !== measure.definition ? { definition: definition.trim() } : {}),
                  ...(gradingRule !== measure.gradingRule
                    ? { gradingRule: gradingRule.trim() }
                    : {}),
                },
                {
                  onSuccess: ({ data }) => {
                    setLabel((cur) => (cur === sent.label ? data.label : cur))
                    setDefinition((cur) => (cur === sent.definition ? data.definition : cur))
                    setGradingRule((cur) => (cur === sent.gradingRule ? data.gradingRule : cur))
                  },
                },
              )
            }}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </div>
  )
}

/** The registry side: groups → families → measures tree, the create modal and the editor. */
function RegistryView({
  selectedId,
  onSelect,
}: {
  selectedId: number | null
  onSelect: (id: number) => void
}) {
  const familiesQuery = useCpgFamilies()
  const measuresQuery = useCpgMeasures()
  const groups = useMemo(() => familiesQuery.data?.data ?? [], [familiesQuery.data])
  const measures = useMemo(() => measuresQuery.data?.data ?? [], [measuresQuery.data])
  const [creating, setCreating] = useState(false)

  const selected = useMemo(
    () => measures.find((m) => m.id === selectedId) ?? null,
    [measures, selectedId],
  )

  // Default to the first measure once loaded.
  useEffect(() => {
    if (selectedId === null && measures.length > 0) onSelect(measures[0].id)
  }, [measures, selectedId, onSelect])

  const loading = familiesQuery.isLoading || measuresQuery.isLoading
  // A failed list must not read as an empty registry: say so and offer a retry.
  const failed = [familiesQuery, measuresQuery].filter((query) => query.isError)

  return (
    <div className="flex h-full min-h-0">
      <aside className="flex w-80 shrink-0 flex-col border-r border-gray-200">
        {failed.length > 0 && (
          <Alert
            m="xs"
            color="red"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            title="Couldn’t load the measure registry"
          >
            <Button
              size="compact-xs"
              variant="light"
              color="red"
              onClick={() => failed.forEach((query) => void query.refetch())}
            >
              Try again
            </Button>
          </Alert>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <MeasureTree
            groups={groups}
            measures={measures}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        </div>
        <div className="shrink-0 border-t border-gray-200 p-2">
          <Button
            fullWidth
            size="compact-sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => setCreating(true)}
          >
            New measure
          </Button>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        {selected ? (
          <MeasureDetail key={selected.id} measure={selected} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {loading
              ? 'Loading…'
              : measuresQuery.isError
                ? 'Couldn’t load measures.'
                : measures.length === 0
                  ? 'No measures yet.'
                  : 'Select a measure.'}
          </div>
        )}
      </main>

      {creating && (
        <CreateMeasureModal
          groups={groups}
          onClose={() => setCreating(false)}
          onCreated={onSelect}
        />
      )}
    </div>
  )
}

/**
 * Super Admin → CPG → Measures (docs/cpg/CPG_ADMIN.md). Registry: measure registry management
 * (PD-1711) — the measure editor (label / definition / grading rule — the two texts are what
 * the Risks & Gaps agent reads — plus retire / reactivate); slug, kind and family are fixed at
 * creation. Proposals: the agent's accumulated measure proposals, clustered and accepted or
 * dismissed (PD-1716). The side and the selected measure live in the URL
 * (`?tab=proposals`, `?measure=<id>`) so a cluster can link to the measure it relates to.
 */
export function MeasuresView() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'proposals' ? 'proposals' : 'registry'
  const measureParam = Number(params.get('measure'))
  const selectedId = Number.isInteger(measureParam) && measureParam > 0 ? measureParam : null

  const selectMeasure = useCallback(
    (id: number) => setParams({ measure: String(id) }, { replace: true }),
    [setParams],
  )

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-gray-200 px-3 py-2">
        <SegmentedControl
          size="xs"
          value={tab}
          onChange={(value) => setParams(value === 'proposals' ? { tab: 'proposals' } : {})}
          data={[
            { value: 'registry', label: 'Registry' },
            { value: 'proposals', label: 'Proposals' },
          ]}
        />
      </div>
      <div className="min-h-0 flex-1">
        {tab === 'registry' ? (
          <RegistryView selectedId={selectedId} onSelect={selectMeasure} />
        ) : (
          <ProposalsView />
        )}
      </div>
    </div>
  )
}
